// packages/ui/src/components/chat/MessageBubble.tsx
// Chat message bubble with user/assistant styling, markdown rendering, and tool results

import { useState } from 'react'

export interface ToolResult {
  tool: string
  params?: Record<string, unknown>
  result?: unknown
  error?: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  toolResults?: ToolResult[]
  isStreaming?: boolean
}

// ─── Simple Markdown Parser ─────────────────────────────────

function parseMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n')
  const nodes: React.ReactNode[] = []
  let inCodeBlock = false
  let codeLines: string[] = []
  let codeLang = ''

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Code block toggle
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        nodes.push(
          <pre
            key={`code-${i}`}
            data-testid="code-block"
            className="bg-gray-950 border border-gray-700 rounded p-3 my-2 overflow-x-auto text-xs font-mono text-gray-300"
          >
            {codeLang && (
              <div className="text-xs text-gray-500 mb-1">{codeLang}</div>
            )}
            <code>{codeLines.join('\n')}</code>
          </pre>,
        )
        codeLines = []
        codeLang = ''
        inCodeBlock = false
      } else {
        inCodeBlock = true
        codeLang = line.slice(3).trim()
      }
      continue
    }

    if (inCodeBlock) {
      codeLines.push(line)
      continue
    }

    // Inline formatting
    nodes.push(
      <span key={`line-${i}`}>
        {i > 0 && !inCodeBlock && <br />}
        {parseInline(line)}
      </span>,
    )
  }

  // Handle unclosed code blocks
  if (inCodeBlock && codeLines.length > 0) {
    nodes.push(
      <pre
        key="code-unclosed"
        data-testid="code-block"
        className="bg-gray-950 border border-gray-700 rounded p-3 my-2 overflow-x-auto text-xs font-mono text-gray-300"
      >
        <code>{codeLines.join('\n')}</code>
      </pre>,
    )
  }

  return nodes
}

function parseInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  // Match: **bold**, `code`, [text](url)
  const regex = /(\*\*(.+?)\*\*)|(`([^`]+?)`)|(\[([^\]]+?)\]\(([^)]+?)\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    if (match[1]) {
      // Bold
      nodes.push(
        <strong key={`b-${match.index}`} className="font-semibold text-gray-100">
          {match[2]}
        </strong>,
      )
    } else if (match[3]) {
      // Inline code
      nodes.push(
        <code
          key={`c-${match.index}`}
          className="bg-gray-800 text-blue-300 px-1 py-0.5 rounded text-xs font-mono"
        >
          {match[4]}
        </code>,
      )
    } else if (match[5]) {
      // Link
      nodes.push(
        <a
          key={`a-${match.index}`}
          href={match[7]}
          className="text-blue-400 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[6]}
        </a>,
      )
    }

    lastIndex = match.index + match[0].length
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}

// ─── Tool Result Component ───────────────────────────────────

function ToolResultSection({ result }: { result: ToolResult }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div data-testid="tool-result" className="mt-2 border border-gray-700 rounded overflow-hidden">
      <button
        data-testid="tool-result-toggle"
        className="w-full flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-xs text-gray-300 hover:bg-gray-750 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="shrink-0">{expanded ? '\u25BC' : '\u25B6'}</span>
        <span className="font-mono">{result.tool}</span>
        {result.error && <span className="text-red-400 ml-auto">error</span>}
      </button>
      {expanded && (
        <div data-testid="tool-result-content" className="px-3 py-2 bg-gray-900 text-xs">
          {result.params && (
            <div className="mb-2">
              <span className="text-gray-500">Params:</span>
              <pre className="text-gray-400 font-mono mt-0.5 whitespace-pre-wrap">
                {JSON.stringify(result.params, null, 2)}
              </pre>
            </div>
          )}
          {result.error ? (
            <div>
              <span className="text-gray-500">Error:</span>
              <pre className="text-red-400 font-mono mt-0.5 whitespace-pre-wrap">{result.error}</pre>
            </div>
          ) : result.result != null ? (
            <div>
              <span className="text-gray-500">Result:</span>
              <pre className="text-gray-300 font-mono mt-0.5 whitespace-pre-wrap">
                {typeof result.result === 'string'
                  ? result.result
                  : JSON.stringify(result.result, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ─── Message Bubble ──────────────────────────────────────────

function formatTime(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

export interface MessageBubbleProps {
  message: ChatMessage
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user'

  return (
    <div
      data-testid={`message-${message.id}`}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div
        className={`max-w-[75%] rounded-lg px-4 py-2.5 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-gray-800 text-gray-200 border border-gray-700'
        }`}
      >
        <div className="text-sm leading-relaxed" data-testid="message-content">
          {isUser ? message.content : parseMarkdown(message.content)}
          {message.isStreaming && (
            <span data-testid="typing-indicator" className="inline-block ml-1 animate-pulse">
              \u2588
            </span>
          )}
        </div>

        {message.toolResults && message.toolResults.length > 0 && (
          <div data-testid="tool-results">
            {message.toolResults.map((tr, idx) => (
              <ToolResultSection key={idx} result={tr} />
            ))}
          </div>
        )}

        <div
          className={`text-xs mt-1.5 ${isUser ? 'text-blue-200' : 'text-gray-500'}`}
          data-testid="message-timestamp"
        >
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  )
}
